export const storageKey = {
  TOKEN: "nmu-token",
  CHAT_TOKEN: "chat-token",
};

export const fileTYPE_IMG = "image/png, image/jpeg, image/jpg, image/gif";

/**
 * NMU-520: the largest image the system accepts, 5 MB.
 *
 * Must stay at or below `client_max_body_size` in the admin-ui nginx configs
 * (devops/{dev,develop,uat,production}/nginx.conf, currently 10M) — that is the
 * limit that actually rejects the request, and it answers 413 with no clue as
 * to which file or why. This constant is what lets the UI say so first, in
 * Thai, before the upload is ever attempted.
 */
export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;

// , text/plain
export const fileTYPE_DOCUMENT =
  ".doc,.docx, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf";
export const fileTYPE_VIDEO =
  "video/mp4, video/mpeg, video/x-msvideo, video/x-flv, video/x-ms-wmp, video/x-ms-wmv, video/x-ms-wmx";

export const groupType = {
  NEWS: 2,
  ACTIVITY: 1,
  BANNER: 0,
  NOTIFICATION: 3,
  SERVICE_LINK: 4,
  TEMPLATE: 5,
  HIGHLIGHT_BOX: 6,
  CONTENT: -1,
  FOOTER_LINK: 7,
  // NMU-473: matches the ordinal of `slider` in nmu-common-service ContentType,
  // which is appended last — see the note on that enum.
  SLIDER: 8,
  // NMU-608: appended after `slider` in that same enum, so ordinal 9.
  QUICK_LINK: 9
};

export const formatDateTime = {
  DDMMYYYY_SLASH: "DD/MM/YYYY",
  DDMMMYYYY_SPACE: "DD MMM YYYY",
  FULLDATETIME: "DD/MM/YYYY HH:mm:ss",
  HOURMINUTE: "HH:mm",
  // NMU-580: the one format every table prints วันที่/เวลาสร้าง in. Seconds are
  // left off deliberately — the column is there to tell records apart by when
  // they were made, and a column that fits is worth more than the second.
  DATETIME_SHORT: "DD/MM/YYYY HH:mm",
};

export const galleryType = {
  VIDEO: 0,
  PHOTO: 1,
  DOCUMENT: 2,
};

/**
 * NMU-470: what a link points at. Mirrors nmu-common-service's MenuType, which
 * the menu editor already uses — a service link offers the same two choices.
 */
export const LINK_TYPE = {
  CONTENT: "CONTENT",
  URL: "URL",
  /**
   * NMU-562: the link opens the News listing filtered to one news category,
   * carried alongside as `newsType`. Mirrors MenuType.NEWS (NMU-559) — the menu
   * editor offers the same choice.
   */
  NEWS: "NEWS",
};

/**
 * NMU-478: the staff-intranet portal sections. Keys must match
 * IntranetCardBean.SECTION_KEYS in nmu-common-service — the API rejects any
 * other value rather than silently creating a section nothing renders.
 */
export const INTRANET_SECTIONS = {
  /**
   * NMU-502: no longer surfaced as a tab — see INTRANET_MODULES.workspace.
   * Kept because the key still exists server-side in
   * IntranetCardBean.SECTION_KEYS, so any legacy record still carries it and the
   * API still returns the (empty) section.
   */
  WORKSPACE_CALENDAR: "workspace_calendar",
  WORKSPACE_DRIVES: "workspace_drives",
  WORKSPACE_SYSTEMS: "workspace_systems",
  HR_ADMINISTRATION: "hr_administration",
  OPERATIONAL_RESOURCES: "operational_resources",
  /** NMU-496: พื้นที่สื่อสารองค์กร on the portal. */
  INTERNAL_COMMUNICATION: "internal_communication",
};

/**
 * NMU-601: the most Workspace & Shortcuts tabs there may be. The service
 * enforces the same number (IntranetWorkspaceTabBean.MAX_TABS); this copy is
 * only what disables + เพิ่มแท็บ before the request is made.
 */
export const INTRANET_MAX_WORKSPACE_TABS = 8;

/** Sub-module → the sections it manages, in the order they appear on the portal. */
export const INTRANET_MODULES = {
  workspace: {
    title: "Workspace & Shortcuts",
    titleTH: "Workspace และทางลัด",
    // NMU-502: ปฏิทินนัดหมาย & กิจกรรม is deliberately absent. Events are managed
    // in Calendar Activity, and since NMU-499 that is also where the portal's
    // calendar widget reads from — nmu-web renders nothing from
    // workspace_calendar at all. Leaving the tab here offered a second place to
    // manage events that no longer fed anything.
    //
    //
    // NMU-601: and the tabs are no longer listed here at all. ทางลัด Google Shared
    // Drives and ระบบงานสำคัญ were the two entries of a fixed list; tabs are now
    // records an editor manages under จัดการแท็บ (up to
    // INTRANET_MAX_WORKSPACE_TABS), fetched from /admin/intranet/tabs. Each one's
    // tabKey is the sectionKey its items are saved under.
    dynamicTabs: true,
    sections: [],
  },
  "hr-admin": {
    title: "HR & Administration",
    titleTH: "ข้อมูลการบริหารและงานบุคคล",
    sections: [{ key: INTRANET_SECTIONS.HR_ADMINISTRATION, label: "ข้อมูลการบริหารและงานบุคคล" }],
  },
  operations: {
    title: "Operational Resources",
    titleTH: "ข้อมูลขับเคลื่อนการทำงาน",
    sections: [{ key: INTRANET_SECTIONS.OPERATIONAL_RESOURCES, label: "ข้อมูลขับเคลื่อนการทำงาน" }],
  },
  // NMU-496: same table, same dialog — only the section it manages differs.
  "internal-comm": {
    title: "Internal Communication",
    titleTH: "พื้นที่สื่อสารองค์กร",
    sections: [{ key: INTRANET_SECTIONS.INTERNAL_COMMUNICATION, label: "พื้นที่สื่อสารองค์กร" }],
  },
};

export const PREVIEW_TYPE = {
  banner: "banner",
  activity: "activity",
  news: "news",
  notification: "notification",
  serviceLink: "serviceLink",
  footerLink: "footerLink",
  highlightBox: "highlightBox",
  mainContent: "mainContent",
  content: "content",
  // NMU-451: the menu editor used to post `contentType: -1` and no content
  // type at all, which the preview endpoint rejects outright.
  menu: "menu",
};

export const chatConnectStatus = {
  NONE: "NONE",
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  SUBSCRIBED: "SUBSCRIBED",
  DISCONNECTED: "DISCONNECTED",
  FAIL: "FAIL",
};

export const DEFAULT_SORT_CONFIG = {
  numeric: true,
  sensitivity: "accent", // non-sensitive
  ignorePunctuation: true,
};

// Content distribution / visibility (Phase 3)
export const VISIBILITY = {
  INTERNAL: "INTERNAL",
  EXTERNAL: "EXTERNAL",
  PUBLIC: "PUBLIC",
};

export const VISIBILITY_DEFAULT = VISIBILITY.PUBLIC;

export const VISIBILITY_OPTIONS = [
  { value: VISIBILITY.INTERNAL, label: "ภายในเท่านั้น (Internal)" },
  { value: VISIBILITY.EXTERNAL, label: "ภายนอกเท่านั้น (External)" },
  { value: VISIBILITY.PUBLIC, label: "สาธารณะ (Public)" },
];

// Activity (calendar) category — drives the public Event Calendar filter tabs
export const ACTIVITY_TYPE = {
  INTERNAL: "INTERNAL",
  SOCIAL: "SOCIAL",
};

export const ACTIVITY_TYPE_OPTIONS = [
  { value: ACTIVITY_TYPE.INTERNAL, label: "กิจกรรมภายในมหาวิทยาลัย (Internal)" },
  { value: ACTIVITY_TYPE.SOCIAL, label: "กิจกรรมบริการสังคม (Social Service)" },
];

// Program (หลักสูตร) degree level — backend field `degreeLevel` (String enum)
export const DEGREE_LEVEL = {
  BACHELOR: "BACHELOR",
  MASTER: "MASTER",
  DOCTORAL: "DOCTORAL",
  CERTIFICATE: "CERTIFICATE",
};

export const DEGREE_LEVEL_OPTIONS = [
  { value: DEGREE_LEVEL.BACHELOR, label: "ปริญญาตรี" },
  { value: DEGREE_LEVEL.MASTER, label: "ปริญญาโท" },
  { value: DEGREE_LEVEL.DOCTORAL, label: "ปริญญาเอก" },
  {
    value: DEGREE_LEVEL.CERTIFICATE,
    label: "ประกาศนียบัตร / หลักสูตรระยะสั้น",
  },
];

// NMU-405/419: the 5 fixed audience personas shown in the website navigation
// bar and used to scope Highlight Box entries and its display config tabs.
export const USER_GROUP_OPTIONS = [
  { value: "prospective", label: "ผู้สนใจเข้าศึกษา" },
  { value: "current", label: "นักศึกษาปัจจุบัน" },
  { value: "researchers", label: "นักวิจัยและพันธมิตร" },
  { value: "alumni", label: "ศิษย์เก่า" },
  { value: "public", label: "บุคคลทั่วไปและหน่วยงานภายนอก" },
];

// Feature config keys (Phase 3)
export const FEATURE_CONFIG_KEYS = {
  GOOGLE_LOGIN_ENABLED: "google_login_enabled",
  USERNAME_LOGIN_ENABLED: "username_login_enabled",
  STAFF_INTRANET_ENABLED: "staff_intranet_enabled",
  EVENT_CALENDAR_ENABLED: "event_calendar_enabled",
  GOOGLE_ALLOWED_HD: "google_allowed_hd",
  // NMU-512: the site's chat launcher offers Facebook Messenger next to the
  // in-site chat. Both keys must match the service seed
  // (FeatureConfigServiceImpl.SEED) and nmu-web's reader — a typo here disables
  // the option silently rather than erroring.
  CHAT_MESSENGER_ENABLED: "chat_messenger_enabled",
  CHAT_MESSENGER_URL: "chat_messenger_url",
};

// Website user role (Phase 3)
export const WEBSITE_USER_ROLE = "WEBSITE_USER";

// User status codes used by the user service (constants.userStatus).
// 'A' = active/enabled, 'I' = inactive/disabled, 'Z' = deleted (hidden in the UI).
export const USER_STATUS = {
  ACTIVE: "A",
  INACTIVE: "I",
  DELETED: "Z",
};

export const COMMON = {
  RAW_DATA: "rawData",
  WARN_FIELD: "warningFields",
  ERROR_FIELD: "errorFields",
  NOTIFICATION_TIMING: 5,
  REM_UP: "remUP",
  TEMPLATE_MARGIN: "160px",
  PAGING_BOTTOM_MARGIN: "24px",
};

/**
 * NMU-564: "Main content" (เนื้อหาหลัก) is not in use, so it is hidden from the
 * sidebar and its route is blocked. The page, its service and its redux action
 * are all left in place — flipping this flag back to true is the only change
 * needed to bring the module back.
 */
export const MAIN_CONTENT_MENU_ENABLED = false;

// Service Link placement group — backend field `category` (ServiceLinkCategory)
// Icon keys a Service Link can carry. The set is fixed by the approved design —
// nmu-web falls back to its own default for anything else.
export const SERVICE_LINK_ICON_OPTIONS = [
  { value: "school", label: "หลักสูตร (school)" },
  { value: "person_add", label: "การรับสมัคร (person_add)" },
  { value: "rewarded_ads", label: "ทุนการศึกษา (rewarded_ads)" },
  { value: "apartment", label: "ชีวิตในมหาวิทยาลัย (apartment)" },
];
